import React from "react";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Stack,
  Typography,
} from "@mui/material";
import { SwapHoriz } from "@mui/icons-material";
import type { VenueBooking, VenueSlot } from "./types";
import DialogHeader from "./DialogHeader";
import {
  formatDate,
  formatTime,
  formatVenueBookingPrice,
  inferSlotLength,
  isValidVenueBooking,
} from "./venueHireUtils";

const isValidVenueSlot = (slot: VenueSlot | null): slot is VenueSlot =>
  Boolean(
    slot &&
      typeof slot.date === "string" &&
      slot.date.length > 0 &&
      typeof slot.start_time === "string" &&
      slot.start_time.length > 0 &&
      typeof slot.end_time === "string" &&
      slot.end_time.length > 0 &&
      typeof slot.price === "number" &&
      Number.isFinite(slot.price),
  );

interface ConfirmVenueRescheduleDialogProps {
  open: boolean;
  booking: VenueBooking | null;
  slot: VenueSlot | null;
  clubName: string;
  confirming: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

const ConfirmVenueRescheduleDialog: React.FC<
  ConfirmVenueRescheduleDialogProps
> = ({
  open,
  booking,
  slot,
  clubName,
  confirming,
  onClose,
  onConfirm,
}) => {
  if (
    !open ||
    !isValidVenueBooking(booking) ||
    !isValidVenueSlot(slot)
  ) {
    return null;
  }

  const slotLength =
    slot.slot_length ?? inferSlotLength(slot.start_time, slot.end_time);
  const paymentRef = booking.invoice_id
    ? `INV-${booking.invoice_id}`
    : undefined;

  return (
    <Dialog
      open={open}
      onClose={() => !confirming && onClose()}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogHeader
        title="Confirm rearrangement"
        subtitle={clubName}
        icon={<SwapHoriz color="primary" />}
        onClose={onClose}
        closeDisabled={confirming}
      />
      <DialogContent dividers>
        <Stack spacing={1.5}>
          <Typography variant="body2" color="text.secondary">
            Move your booking to this slot:
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            {formatDate(slot.date)}
          </Typography>
          <Typography variant="body1">
            {formatTime(slot.start_time)} – {formatTime(slot.end_time)} (
            {slotLength} min)
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 700 }}>
            {formatVenueBookingPrice(slot.price)}
          </Typography>
          <Alert severity="success">
            No further payment is needed. Your existing payment reference
            {paymentRef ? ` (${paymentRef})` : ""} stays the same.
          </Alert>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={confirming}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          disabled={confirming}
          startIcon={
            confirming ? (
              <CircularProgress size={18} color="inherit" />
            ) : undefined
          }
        >
          {confirming ? "Moving…" : "Move booking"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmVenueRescheduleDialog;
